import React from "react";
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface ReferralFeedbackFormProps {
  formValues: Record<string, any>;
  onFieldChange: (fieldId: string, value: any) => void;
  className?: string;
}

const REFERRAL_ROWS = 8;

export function ReferralFeedbackForm({
  formValues,
  onFieldChange,
  className,
}: ReferralFeedbackFormProps) {
  const referrals: Array<{ name: string; alter: string; telefon: string }> = 
    formValues.referrals || Array(REFERRAL_ROWS).fill({ name: "", alter: "", telefon: "" });
  const notes: string = formValues.referral_notes || "";

  const updateReferral = (index: number, field: 'name' | 'alter' | 'telefon', value: string) => {
    const updated = [...referrals];
    if (!updated[index]) {
      updated[index] = { name: "", alter: "", telefon: "" };
    }
    updated[index] = { ...updated[index], [field]: value };
    onFieldChange("referrals", updated);
  };

  return (
    <div className={cn("p-6 space-y-8", className)}>
      {/* Title */}
      <div className="text-left">
        <h2 className="text-2xl md:text-3xl font-bold">
          <span className="text-foreground">Weiter</span>
          <span className="text-primary">empfehlungen</span>
        </h2>
        <p className="text-lg italic text-muted-foreground mt-1">
          Damit auch Ihr Umfeld profitieren kann.
        </p>
      </div>

      {/* Referrals Table */}
      <div className="space-y-4">
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-primary text-primary-foreground">
                <th className="text-left p-3 font-semibold">Name</th>
                <th className="text-left p-3 font-semibold w-24">Alter</th>
                <th className="text-left p-3 font-semibold w-40">Telefon</th>
              </tr>
            </thead>
            <tbody>
              {Array(REFERRAL_ROWS).fill(null).map((_, index) => (
                <tr key={index} className="border-t">
                  <td className="p-2">
                    <Input
                      value={referrals[index]?.name || ""}
                      onChange={(e) => updateReferral(index, 'name', e.target.value)}
                      className="h-8 text-sm border-0 shadow-none"
                      placeholder=""
                    />
                  </td>
                  <td className="p-2">
                    <Input
                      value={referrals[index]?.alter || ""}
                      onChange={(e) => updateReferral(index, 'alter', e.target.value)}
                      className="h-8 text-sm border-0 shadow-none"
                      placeholder=""
                    />
                  </td>
                  <td className="p-2">
                    <Input
                      value={referrals[index]?.telefon || ""}
                      onChange={(e) => updateReferral(index, 'telefon', e.target.value)}
                      className="h-8 text-sm border-0 shadow-none"
                      placeholder=""
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Notes Section */}
      <div className="space-y-3">
        <div className="border-2 border-muted rounded-lg p-4">
          <Label className="text-sm font-semibold text-foreground">Notizen:</Label>
          <Textarea
            value={notes}
            onChange={(e) => onFieldChange("referral_notes", e.target.value)}
            placeholder="Ihre Notizen hier..."
            className="mt-2 min-h-[120px] resize-none"
          />
        </div>
      </div>
    </div>
  );
}
